import type { ControlBridgeViewModel } from '../deriveViewModel';
import type { DiagnoseScopeHealthRow } from '../deriveDiagnoseHealth';
import { entropyFromCorrectionFactor, entropyStateLabel } from '../entropyLanguage';
import { queueItemMatchesStructureScopeId } from '../../../utils/scopeIdPlane';

export interface VgrHistoryPoint {
  periodLabel: string;
  /** ISO week start (YYYY-MM-DD) when supplied by manifest */
  periodStart?: string;
  correctionFactor: number | null;
  entropy: number | null;
  kind?: 'history' | 'today' | 'projection';
}

export interface VgrHistorySeries {
  points: VgrHistoryPoint[];
  source: 'manifest' | 'proxy';
}

const PROXY_BUCKET_COUNT = 8;

const PROXY_LABELS = ['8w ago', '7w ago', '6w ago', '5w ago', '4w ago', '3w ago', '2w ago', 'Now'];

function hashSeed(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function clampCf(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** Deterministic proxy buckets ending at the row's current CF / entropy. */
export function proxyVgrHistory(row: DiagnoseScopeHealthRow): VgrHistorySeries {
  const endCf = row.correctionFactor ?? 0.55;
  const endEntropy = row.entropy ?? entropyFromCorrectionFactor(endCf);
  const seed = hashSeed(row.scopeId);
  const startCf = clampCf(endCf - 0.12 - (seed % 40) / 200);

  const points: VgrHistoryPoint[] = [];
  const span = PROXY_BUCKET_COUNT - 1;
  for (let i = 0; i < PROXY_BUCKET_COUNT; i += 1) {
    const t = span <= 0 ? 1 : i / span;
    const wobble = i === PROXY_BUCKET_COUNT - 1 ? 0 : ((hashSeed(`${row.scopeId}:${i}`) % 15) - 7) / 400;
    const cf = clampCf(startCf + (endCf - startCf) * t + wobble);
    const entropy =
      i === PROXY_BUCKET_COUNT - 1
        ? endEntropy
        : entropyFromCorrectionFactor(cf);
    points.push({
      periodLabel: PROXY_LABELS[i] ?? `-${PROXY_BUCKET_COUNT - i}w`,
      correctionFactor: cf,
      entropy,
    });
  }

  return { points, source: 'proxy' };
}

function manifestHistoryPoints(
  vm: ControlBridgeViewModel,
  scopeId: string
): VgrHistoryPoint[] | null {
  const row = vm.scopeHealthQueue.find((item) =>
    queueItemMatchesStructureScopeId(item, scopeId)
  );
  if (!row) return null;

  const raw = row as typeof row & {
    vgrHistory?: Array<{
      periodLabel?: string;
      periodStart?: string;
      correctionFactor?: number;
      entropy?: number;
    }>;
  };

  if (!raw.vgrHistory?.length) return null;

  return raw.vgrHistory.map((point, index) => {
    const cf =
      typeof point.correctionFactor === 'number' ? clampCf(point.correctionFactor) : null;
    const rawEntropy = typeof point.entropy === 'number' ? point.entropy : null;
    const entropy =
      cf != null
        ? entropyFromCorrectionFactor(cf)
        : entropyStateLabel(rawEntropy) != null
          ? rawEntropy
          : rawEntropy != null && rawEntropy >= 1 && rawEntropy <= 7
            ? rawEntropy
            : null;
    return {
      periodLabel: point.periodLabel ?? point.periodStart ?? `P${index + 1}`,
      periodStart: point.periodStart,
      correctionFactor: cf,
      entropy,
    };
  });
}

export function deriveVgrHistory(
  row: DiagnoseScopeHealthRow,
  vm: ControlBridgeViewModel
): VgrHistorySeries {
  const fromManifest = manifestHistoryPoints(vm, row.scopeId);
  if (fromManifest?.length) {
    const history = fromManifest.map((point, index) => ({
      ...point,
      kind: (index === fromManifest.length - 1 ? 'today' : 'history') as VgrHistoryPoint['kind'],
    }));
    const last = history[history.length - 1];
    history.push({
      periodLabel: 'Projection',
      correctionFactor: last.correctionFactor,
      entropy: last.entropy,
      kind: 'projection',
    });
    return { points: history, source: 'manifest' };
  }
  if (row.correctionFactor != null || row.entropy != null) {
    return {
      points: [
        {
          periodLabel: 'Projection',
          correctionFactor: row.correctionFactor,
          entropy: row.entropy,
          kind: 'projection',
        },
      ],
      source: 'proxy',
    };
  }
  return { points: [], source: 'proxy' };
}

export function vgrHistoryChartSummary(series: VgrHistorySeries): string {
  const last = series.points[series.points.length - 1];
  const cfPct =
    last?.correctionFactor != null ? `${Math.round(last.correctionFactor * 100)}%` : 'unknown';
  const prefix = series.source === 'manifest' ? 'VGR trend' : 'Estimated VGR trend';
  return `${prefix} ending at ${cfPct} operating efficiency across ${series.points.length} periods`;
}
